/**
 * Phase 19 — Server-Sent Events client hook.
 *
 * Opens a single ``EventSource`` connection authenticated by a
 * short-lived stream token (POST /api/events/token, 5 min TTL). The
 * main session JWT is NEVER put in the EventSource URL.
 *
 * Usage:
 *   const lastEvent = useEventStream(sessionToken);
 *   useEffect(() => { if (lastEvent?.type === 'notification') ... },
 *             [lastEvent]);
 *
 * The hook returns the most recent event (`{type, data}`) plus a
 * connection-status string. Each new event triggers a re-render.
 *
 * Auth model:
 *   - On mount (and on session token change), POST /events/token to
 *     get a fresh ~5 min ``stream_token``.
 *   - Open EventSource with ``?stream_token=<...>``.
 *   - 60 s before token expiry (when expires_in is 300, that's 240 s
 *     after open), refresh the stream_token and re-open the stream.
 *
 * Reconnect / fallback:
 *   - EventSource auto-reconnects on transient drops.
 *   - We cap at MAX_RECONNECTS in any 60 s window; beyond that we
 *     stop and emit a status ``'down'`` so the caller can fall back
 *     to its existing fetch path.
 *   - On ``visibilitychange`` to visible after a long hide, the caller
 *     is responsible for a one-shot refetch (App.js does this).
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Refresh stream-token this many seconds before expiry.
const TOKEN_REFRESH_LEAD_SECONDS = 60;

// Hard ceiling on reconnect attempts within RECONNECT_WINDOW_MS.
const MAX_RECONNECTS = 8;
const RECONNECT_WINDOW_MS = 60 * 1000;

/**
 * @param {string|null} sessionToken - The Bearer JWT for the user.
 * @returns {{ lastEvent: object|null, status: string }}
 */
export default function useEventStream(sessionToken) {
  const [lastEvent, setLastEvent] = useState(null);
  const [status, setStatus] = useState('idle'); // 'idle' | 'connecting' | 'open' | 'down'
  const sourceRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const reconnectsRef = useRef([]); // timestamps of recent reconnects

  const closeStream = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
  }, []);

  const openStream = useCallback(
    async (token) => {
      if (!token) return;
      // Mint a short-lived stream token; the main JWT never lands in
      // the URL.
      let streamToken;
      let expiresInSec;
      try {
        const r = await axios.post(
          `${API}/events/token`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        streamToken = r.data?.stream_token;
        expiresInSec = r.data?.expires_in || 300;
      } catch {
        setStatus('down');
        return;
      }
      if (!streamToken) {
        setStatus('down');
        return;
      }

      closeStream();

      const url = `${API}/events/stream?stream_token=${encodeURIComponent(streamToken)}`;
      const es = new EventSource(url);
      sourceRef.current = es;
      setStatus('connecting');

      es.onopen = () => setStatus('open');

      es.onerror = () => {
        // EventSource auto-reconnects on most failures. Track frequency
        // so we can shut down a runaway loop and let the caller fall
        // back to its on-mount fetch.
        const now = Date.now();
        reconnectsRef.current = reconnectsRef.current
          .filter((t) => now - t < RECONNECT_WINDOW_MS);
        reconnectsRef.current.push(now);
        if (reconnectsRef.current.length > MAX_RECONNECTS) {
          closeStream();
          setStatus('down');
        }
      };

      // Subscribe to typed events. We don't bind to the catch-all
      // ``onmessage`` because the server always sends an explicit
      // ``event:`` line.
      const types = ['hello', 'notification', 'unread_counts', 'message_new', 'typing'];
      types.forEach((t) => {
        es.addEventListener(t, (evt) => {
          try {
            const data = evt.data ? JSON.parse(evt.data) : null;
            setLastEvent({ type: t, data, ts: Date.now() });
          } catch {
            // Ignore malformed payloads.
          }
        });
      });

      // Schedule a refresh ~60 s before this stream-token expires.
      const refreshInMs = Math.max(
        15_000,
        (expiresInSec - TOKEN_REFRESH_LEAD_SECONDS) * 1000,
      );
      refreshTimerRef.current = setTimeout(() => {
        openStream(token);
      }, refreshInMs);
    },
    [closeStream],
  );

  useEffect(() => {
    if (!sessionToken) {
      closeStream();
      setStatus('idle');
      return undefined;
    }
    reconnectsRef.current = [];
    openStream(sessionToken);
    return () => closeStream();
  }, [sessionToken, openStream, closeStream]);

  return { lastEvent, status };
}
